import React, { createContext, useContext, useEffect, useState } from 'react';

import { isRecord, textOf, unwrapList, unwrapRecord } from './api-client';
import type { AdminApiClient, JsonRecord } from './api-client';
import { pathFor } from './page-registry';
import './canonical-round04-layout.css';
import { AUTHORIZED_SCOPE } from './authorized-scope-client';
import { configurationScopeAppId } from './canonical-admin';

type Params = Record<string, string>;
type LoadState<T = unknown> = { data?: T; error?: unknown; loading: boolean; reload: () => void };

interface PageProps {
  api: AdminApiClient;
  targetAppId: string;
  pageId: string;
  params: Params;
}

const statusText: Record<string, string> = {
  ACTIVE: '已启用',
  ENABLED: '正常可用',
  DISABLED: '已停用',
  PENDING_VERIFY: '待验证',
  OBSERVE: '观察中',
  DEGRADED: '已降级',
  NORMAL: '正常',
  SUCCEEDED: '支付成功',
  SUCCESS: '支付成功',
  PENDING: '待确认',
  FAILED: '系统失败',
  CLOSED: '未支付',
  AUTHORIZED: '已授权',
  SYSTEM: '系统自动',
  USER: '用户操作',
};

const eventText: Record<string, string> = {
  app_open: '打开小程序',
  city_view: '浏览城市首页',
  city_switch: '切换城市',
  product_impression: '看到产品推荐',
  product_view: '查看产品详情',
  book_click: '点击立即预订',
  order_draft_created: '创建待支付订单',
  pay_invoke: '调起微信支付',
  pay_cancel_return: '取消支付后返回',
  pay_fail_return: '支付失败后返回',
  pay_success_confirmed: '服务端确认支付成功',
  refund_success: '退款成功',
  wecom_add_click: '点击添加企业微信',
};

function useTask<T>(key: string, loader: () => Promise<T>, dependency: unknown): LoadState<T> {
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState<Omit<LoadState<T>, 'reload'>>({ loading: true });
  useEffect(() => {
    let active = true;
    setState({ loading: true });
    void loader().then(
      (data) => active && setState({ loading: false, data }),
      (error: unknown) => active && setState({ loading: false, error }),
    );
    return () => {
      active = false;
    };
  }, [key, revision, dependency]);
  return { ...state, reload: () => setRevision((value) => value + 1) };
}

function useApi(api: AdminApiClient, path?: string) {
  return useTask(
    path ?? 'round04-disabled',
    () => (path ? api.get(path) : Promise.resolve({})),
    api,
  );
}

function rows(value: unknown): JsonRecord[] {
  return unwrapList(value);
}

function nested(record: JsonRecord, key: string): JsonRecord {
  return isRecord(record[key]) ? record[key] : {};
}

function nestedRows(record: JsonRecord, key: string): JsonRecord[] {
  return Array.isArray(record[key]) ? record[key].filter(isRecord) : [];
}

function label(value: unknown, fallback = '待确认') {
  if (typeof value !== 'string' || !value) return fallback;
  return statusText[value] ?? fallback;
}

function tone(value: unknown) {
  const raw = String(value);
  if (['ACTIVE', 'ENABLED', 'NORMAL', 'SUCCEEDED', 'SUCCESS', 'AUTHORIZED'].includes(raw))
    return 'green';
  if (['FAILED', 'DISABLED'].includes(raw)) return 'red';
  if (['OBSERVE', 'DEGRADED', 'PENDING_VERIFY', 'PENDING'].includes(raw)) return 'orange';
  return 'gray';
}

function Pill({ value, text }: { value: unknown; text?: string }) {
  return <span className={`canonical-pill ${tone(value)}`}>{text ?? label(value)}</span>;
}

function dateTime(value: unknown) {
  if (typeof value !== 'string') return '暂未记录';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '暂未记录';
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function money(value: unknown) {
  const cents = Number(value);
  return Number.isFinite(cents)
    ? `¥${(cents / 100).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '暂未记录';
}

function shortRef(value: unknown, prefix: string) {
  if (typeof value !== 'string' || !value) return `${prefix} · 待补充`;
  const compact = value.replaceAll('-', '');
  return `${prefix} · ${compact.slice(-8).toUpperCase()}`;
}

function safeText(value: unknown, fallback = '暂未记录') {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return fallback;
}

function Header({
  title,
  actions,
}: {
  title: string;
  subtitle: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="canonical-page-title">
      <div>
        <h1>{title}</h1>
      </div>
      {actions && <div className="canonical-page-actions">{actions}</div>}
    </div>
  );
}

function navigate(event: React.MouseEvent<HTMLAnchorElement>, href: string) {
  if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  history.pushState({}, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function NavLink({
  href,
  children,
  className,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <a href={href} className={className} onClick={(event) => navigate(event, href)}>
      {children}
    </a>
  );
}

function Notice({ children, warning = false }: { children: React.ReactNode; warning?: boolean }) {
  return <div className={`r4-notice ${warning ? 'warning' : ''}`}>{children}</div>;
}

function Metrics({
  items,
}: {
  items: Array<{ label: string; value: React.ReactNode; note?: string }>;
}) {
  return (
    <div className={`r4-metrics ${items.length > 5 ? 'six' : ''}`}>
      {items.map((item) => (
        <article key={item.label}>
          <span>{item.label}</span>
          <strong>{item.value}</strong>
          {item.note && <small>{item.note}</small>}
        </article>
      ))}
    </div>
  );
}

function Panel({
  title,
  hint,
  children,
  className = '',
  toolbar,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
  toolbar?: React.ReactNode;
}) {
  return (
    <section className={`r4-panel ${className}`}>
      <header>
        <h2>{title}</h2>
        {hint && <span>{hint}</span>}
      </header>
      {toolbar && <div className="r4-panel-toolbar">{toolbar}</div>}
      <div className="r4-panel-body">{children}</div>
    </section>
  );
}

function State({
  state,
  count,
  noun,
  children,
}: {
  state: LoadState;
  count: number;
  noun: string;
  children: React.ReactNode;
}) {
  if (state.loading) return <div className="r4-state">正在加载{noun}…</div>;
  if (state.error)
    return (
      <div className="r4-state error" role="alert">
        <strong>{noun}读取失败</strong>
        <span>请检查网络或权限后重试。</span>
        <button onClick={state.reload}>重新加载</button>
      </div>
    );
  if (count === 0) return <div className="r4-state">当前范围暂无{noun}</div>;
  return <>{children}</>;
}

function Filters({
  search,
  setSearch,
  placeholder = '输入名称或业务编号',
  children,
}: {
  search: string;
  setSearch: (value: string) => void;
  placeholder?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="r4-filters">
      <input
        type="search"
        aria-label={placeholder}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={placeholder}
      />
      {children}
      {search && <button onClick={() => setSearch('')}>清空筛选</button>}
    </div>
  );
}

function ProgressList({
  items,
}: {
  items: Array<{ label: string; value: number; note?: string; color?: string }>;
}) {
  const max = Math.max(1, ...items.map((item) => item.value));
  return (
    <div className="r4-progress-list">
      {items.map((item) => (
        <div key={item.label}>
          <div>
            <span>{item.label}</span>
            <strong>{item.note ?? item.value.toLocaleString('zh-CN')}</strong>
          </div>
          <i>
            <b
              style={{ width: `${Math.max(0, (item.value / max) * 100)}%`, background: item.color }}
            />
          </i>
        </div>
      ))}
    </div>
  );
}

function profileName(member: JsonRecord) {
  const profile = nested(member, 'profile');
  return safeText(profile.nickname, '匿名会员');
}

function tags(member: JsonRecord) {
  return nestedRows(member, 'tags')
    .map((tag) => safeText(tag.name, ''))
    .filter(Boolean);
}

function memberState(member: JsonRecord) {
  return textOf(member, 'status') === 'ACTIVE' ? '已启用' : '已停用';
}

function MemberTabs({ pageId }: { pageId: string }) {
  const tabs: Array<[string, string]> = [
    ['ADM-F01', '会员列表'],
    ['ADM-F04', '标签与分群'],
    ['ADM-F05', '转化漏斗'],
    ['ADM-F06', '偏好分析'],
    ['ADM-F07', '支付放弃'],
    ['ADM-F08', '导出与隐私'],
  ];
  return (
    <nav className="r4-tabs">
      {tabs.map(([id, text]) => (
        <a key={id} className={pageId === id ? 'active' : ''} href={pathFor(id)}>
          {text}
        </a>
      ))}
    </nav>
  );
}

function MemberTable({ members }: { members: JsonRecord[] }) {
  return (
    <div className="table-wrap r4-table">
      <table>
        <thead>
          <tr>
            <th>会员</th>
            <th>身份状态</th>
            <th>首次来源</th>
            <th>首次 / 最近访问</th>
            <th>标签</th>
            <th>跨小程序身份</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {members.map((member) => {
            const id = textOf(member, 'id');
            const linked = nestedRows(member, 'linkedApps');
            return (
              <tr key={id}>
                <td>
                  <div className="r4-person">
                    <b>{profileName(member).slice(0, 1)}</b>
                    <span>
                      <strong>{profileName(member)}</strong>
                      <small>{shortRef(id, '会员')}</small>
                    </span>
                  </div>
                </td>
                <td>
                  <Pill value={textOf(member, 'status')} text={memberState(member)} />
                </td>
                <td>
                  {textOf(member, 'firstCityId') === '—' ? '待归因' : '已记录城市来源'}
                  <small className="r4-sub">
                    {safeText(
                      member.appName ?? member.sourceAppName ?? nested(member, 'app').displayName,
                      '来源小程序未标注',
                    )}
                  </small>
                </td>
                <td>
                  {dateTime(member.firstSeenAt)}
                  <small className="r4-sub">最近 {dateTime(member.lastSeenAt)}</small>
                </td>
                <td>
                  <div className="r4-tags">
                    {tags(member).length ? (
                      tags(member).map((tag) => <em key={tag}>{tag}</em>)
                    ) : (
                      <span>暂无标签</span>
                    )}
                  </div>
                </td>
                <td>
                  {linked.length} 个小程序身份<small className="r4-sub">仅显示已合并计数</small>
                </td>
                <td>
                  <a href={pathFor('ADM-F02', { id })}>360详情</a>{' '}
                  <a href={pathFor('ADM-F03', { id })}>行为轨迹</a>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MemberList({
  pageId,
  membersState,
  appId,
}: {
  pageId: string;
  membersState: LoadState;
  appId: string;
}) {
  const aggregate = appId === AUTHORIZED_SCOPE;
  const all = rows(membersState.data);
  const [search, setSearch] = useState('');
  const visible = all.filter((member) =>
    `${profileName(member)} ${shortRef(member.id, '')}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  return (
    <div className="r4-page r4-workspace">
      <Header title="会员列表" subtitle="会员 / CDP　/　会员列表" />
      {aggregate && (
        <Notice>
          会员列表按当前账号的授权范围读取，以下状态和活跃分组只统计本页记录，不代表全部授权会员总量。
        </Notice>
      )}
      <Metrics
        items={[
          { label: aggregate ? '本页会员' : '当前会员', value: all.length, note: '当前授权范围' },
          {
            label: '启用会员',
            value: all.filter((item) => textOf(item, 'status') === 'ACTIVE').length,
          },
          {
            label: '已授权资料',
            value: all.filter((item) => Boolean(nested(item, 'profile').authorizedAt)).length,
          },
          {
            label: '近 7 日活跃',
            value: all.filter(
              (item) => Date.now() - new Date(String(item.lastSeenAt)).getTime() <= 604800000,
            ).length,
          },
          {
            label: '跨小程序身份',
            value: all.reduce((sum, item) => sum + nestedRows(item, 'linkedApps').length, 0),
          },
        ]}
      />
      <MemberTabs pageId={pageId} />
      <Panel
        title="会员列表"
        hint={`当前显示 ${visible.length} 位会员`}
        toolbar={<Filters search={search} setSearch={setSearch} placeholder="会员昵称或会员编号" />}
      >
        <State state={membersState} count={visible.length} noun="会员">
          <MemberTable members={visible} />
        </State>
      </Panel>
    </div>
  );
}

function MemberDetail({
  api,
  appId,
  member,
  eventsState,
  reload,
}: {
  api: AdminApiClient;
  appId: string;
  member: JsonRecord;
  eventsState: LoadState;
  reload: () => void;
}) {
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteError, setNoteError] = useState('');
  const events = rows(eventsState.data);
  const notes = Array.isArray(member.notes) ? member.notes.filter(isRecord) : [];
  const linked = nestedRows(member, 'linkedApps');
  const analytics = nested(member, 'analytics');
  const name = profileName(member);
  const initials = name.slice(0, 1);
  const eventCounts = new Map<string, number>();
  events.forEach((event) =>
    eventCounts.set(
      textOf(event, 'eventName'),
      (eventCounts.get(textOf(event, 'eventName')) ?? 0) + 1,
    ),
  );
  return (
    <div className="r4-page r4-workspace">
      <Header
        title="会员360详情"
        subtitle="会员 / CDP　/　会员360详情"
        actions={
          <a className="r3-button-link" href={pathFor('ADM-F03', { id: textOf(member, 'id') })}>
            查看行为时间线
          </a>
        }
      />
      <section className="r4-member-hero">
        <b>{initials}</b>
        <div>
          <h2>
            {name} <Pill value={textOf(member, 'status')} text={memberState(member)} />
          </h2>
          <p>
            {shortRef(member.id, '会员')} · 首次访问 {dateTime(member.firstSeenAt)}
          </p>
          <div className="r4-tags">
            {tags(member).map((tag) => (
              <em key={tag}>{tag}</em>
            ))}
          </div>
        </div>
      </section>
      <Metrics
        items={[
          {
            label: '累计订单',
            value: Number(analytics.orderCount ?? 0),
          },
          { label: '累计成交', value: money(analytics.payAmountCents) },
          { label: '行为事件', value: Number(analytics.eventCount ?? 0) },
          { label: '优惠金额', value: money(analytics.discountAmountCents) },
          { label: '跨小程序身份', value: linked.length },
        ]}
      />
      <div className="r4-detail-layout">
        <div>
          <div className="r4-two">
            <Panel title="跨小程序身份映射" hint="仅显示安全摘要">
              <div className="table-wrap r4-table">
                <table>
                  <thead>
                    <tr>
                      <th>小程序</th>
                      <th>首次访问</th>
                      <th>最近访问</th>
                      <th>合并状态</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linked.map((identity, index) => (
                      <tr key={`${textOf(identity, 'appId')}-${index}`}>
                        <td>关联小程序 {index + 1}</td>
                        <td>{dateTime(identity.firstSeenAt)}</td>
                        <td>{dateTime(identity.lastSeenAt)}</td>
                        <td>
                          <Pill value="ENABLED" text="已安全关联" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
            <Panel title="授权资料与归因" hint="敏感字段已脱敏">
              <dl className="r4-kv">
                <div>
                  <dt>会员昵称</dt>
                  <dd>{name}</dd>
                </div>
                <div>
                  <dt>首次来源</dt>
                  <dd>当前小程序</dd>
                </div>
                <div>
                  <dt>首次城市</dt>
                  <dd>{textOf(member, 'firstCityId') === '—' ? '暂未归因' : '已记录'}</dd>
                </div>
                <div>
                  <dt>资料授权</dt>
                  <dd>
                    {nested(member, 'profile').authorizedAt
                      ? dateTime(nested(member, 'profile').authorizedAt)
                      : '尚未授权'}
                  </dd>
                </div>
              </dl>
            </Panel>
          </div>
          <Panel title="最近行为" hint="最近 6 条">
            <div className="r4-timeline compact">
              {events.slice(0, 6).map((event) => (
                <article key={textOf(event, 'eventId')}>
                  <i />
                  <div>
                    <time>{dateTime(event.eventTime)}</time>
                    <strong>{eventText[textOf(event, 'eventName')] ?? '会员行为'}</strong>
                    <span>
                      {textOf(event, 'pageId') === '—' ? '页面信息未记录' : '已记录页面来源'}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          </Panel>
        </div>
        <aside>
          <Panel title="偏好画像">
            <ProgressList
              items={Array.from(eventCounts.entries())
                .slice(0, 4)
                .map(([key, value]) => ({ label: eventText[key] ?? '其他行为', value }))}
            />
          </Panel>
          <Panel title="内部运营">
            <dl className="r4-kv">
              <div>
                <dt>最后备注</dt>
                <dd>{notes[0] ? safeText(notes[0].content, '暂无内部备注') : '暂无内部备注'}</dd>
              </div>
            </dl>
            <label>
              新增内部备注
              <textarea
                value={note}
                maxLength={2000}
                placeholder="仅填写履约或服务所需的内部信息，不记录支付密钥等敏感材料"
                onChange={(event) => setNote(event.target.value)}
              />
            </label>
            <button
              className="primary"
              disabled={savingNote || !note.trim()}
              onClick={() => {
                setSavingNote(true);
                setNoteError('');
                void api
                  .send(
                    `/api/admin/m5/members/${encodeURIComponent(textOf(member, 'id'))}/notes?appId=${encodeURIComponent(appId)}`,
                    'POST',
                    { content: note.trim() },
                  )
                  .then(() => {
                    setNote('');
                    reload();
                  })
                  .catch((error: unknown) =>
                    setNoteError(error instanceof Error ? error.message : '备注保存失败'),
                  )
                  .finally(() => setSavingNote(false));
              }}
            >
              {savingNote ? '保存中…' : '保存备注'}
            </button>
            {noteError && <small className="r4-help error">{noteError}</small>}
          </Panel>
        </aside>
      </div>
    </div>
  );
}

function EventTimeline({ member, eventsState }: { member: JsonRecord; eventsState: LoadState }) {
  const events = rows(eventsState.data);
  const [selectedId, setSelectedId] = useState('');
  const [rangeDays, setRangeDays] = useState('30');
  const [eventName, setEventName] = useState('ALL');
  const cutoff = rangeDays === 'ALL' ? null : Date.now() - Number(rangeDays) * 86_400_000;
  const filteredEvents = events.filter((event) => {
    const happenedAt = new Date(String(event.eventTime ?? '')).getTime();
    const withinRange = cutoff === null || (Number.isFinite(happenedAt) && happenedAt >= cutoff);
    return withinRange && (eventName === 'ALL' || textOf(event, 'eventName') === eventName);
  });
  const eventNames = [...new Set(events.map((event) => textOf(event, 'eventName')))].filter(
    (value) => value !== '—',
  );
  const selected =
    filteredEvents.find((event) => textOf(event, 'eventId') === selectedId) ??
    filteredEvents[0] ??
    {};
  return (
    <div className="r4-page r4-workspace">
      <Header title="会员行为时间线" subtitle="会员 / CDP　/　行为时间线" />
      <div className="r4-filters">
        <span>{shortRef(member.id, '会员')}</span>
        <select
          aria-label="时间范围"
          value={rangeDays}
          onChange={(event) => setRangeDays(event.target.value)}
        >
          <option value="7">最近 7 天</option>
          <option value="30">最近 30 天</option>
          <option value="ALL">全部时间</option>
        </select>
        <select
          aria-label="行为类型"
          value={eventName}
          onChange={(event) => setEventName(event.target.value)}
        >
          <option value="ALL">全部行为</option>
          {eventNames.map((name) => (
            <option key={name} value={name}>
              {eventText[name] ?? name}
            </option>
          ))}
        </select>
        <span className="r3-source-note">即时筛选 · {filteredEvents.length} 条</span>
      </div>
      <div className="r4-side-layout">
        <Panel
          title={`行为时间线 · ${profileName(member)}`}
          hint={`当前显示 ${filteredEvents.length} 个事件`}
        >
          <State state={eventsState} count={filteredEvents.length} noun="行为事件">
            <div className="r4-timeline">
              {filteredEvents.map((event) => (
                <button
                  className={
                    textOf(selected, 'eventId') === textOf(event, 'eventId') ? 'selected' : ''
                  }
                  key={textOf(event, 'eventId')}
                  onClick={() => setSelectedId(textOf(event, 'eventId'))}
                >
                  <i />
                  <div>
                    <time>{dateTime(event.eventTime)}</time>
                    <strong>{eventText[textOf(event, 'eventName')] ?? '会员行为'}</strong>
                    <span>{safeText(event.pageId, '来源页面未记录')}</span>
                  </div>
                </button>
              ))}
            </div>
          </State>
        </Panel>
        <aside>
          <Panel title="选中事件详情">
            <dl className="r4-kv">
              <div>
                <dt>行为</dt>
                <dd>{eventText[textOf(selected, 'eventName')] ?? '会员行为'}</dd>
              </div>
              <div>
                <dt>发生时间</dt>
                <dd>{dateTime(selected.eventTime)}</dd>
              </div>
              <div>
                <dt>来源页面</dt>
                <dd>{safeText(selected.pageId)}</dd>
              </div>
              <div>
                <dt>城市信息</dt>
                <dd>{selected.cityId ? '已记录' : '未记录'}</dd>
              </div>
              <div>
                <dt>产品信息</dt>
                <dd>{selected.productId ? '已记录' : '未记录'}</dd>
              </div>
            </dl>
          </Panel>
        </aside>
      </div>
    </div>
  );
}

function TagSegments({
  api,
  appId,
  membersState,
  tagsState,
  segmentsState,
}: {
  api: AdminApiClient;
  appId: string;
  membersState: LoadState;
  tagsState: LoadState;
  segmentsState: LoadState;
}) {
  const members = rows(membersState.data);
  const tagRows = rows(tagsState.data);
  const segmentRows = rows(segmentsState.data);
  const [memberId, setMemberId] = useState('');
  const [tagName, setTagName] = useState('');
  const [segmentName, setSegmentName] = useState('');
  const [eventName, setEventName] = useState('pay_cancel_return');
  const [message, setMessage] = useState('');
  const [tagMessage, setTagMessage] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!memberId && members[0]) setMemberId(textOf(members[0], 'id'));
  }, [memberId, members]);
  async function assignTag() {
    if (!memberId || !tagName.trim() || busy) return;
    setBusy(true);
    setTagMessage('');
    try {
      await api.send(
        `/api/admin/m5/members/${encodeURIComponent(memberId)}/tags?appId=${encodeURIComponent(appId)}`,
        'POST',
        { name: tagName.trim() },
      );
      setTagName('');
      setTagMessage('标签已保存并写入审计日志');
      membersState.reload();
      tagsState.reload();
    } catch {
      setTagMessage('标签保存失败，请检查权限或会员范围');
    } finally {
      setBusy(false);
    }
  }
  async function createSegment() {
    if (!segmentName.trim() || busy) return;
    setBusy(true);
    setMessage('');
    try {
      await api.send(`/api/admin/m5/segments?appId=${encodeURIComponent(appId)}`, 'POST', {
        code: `SEG_${Date.now()}`,
        name: segmentName.trim(),
        rules: {
          match: 'ANY',
          tagCodes: [],
          eventNames: [eventName],
          cityIds: [],
          productIds: [],
        },
      });
      setSegmentName('');
      setMessage('分群规则已持久化并按当前 App 计算');
      segmentsState.reload();
    } catch {
      setMessage('分群创建失败，请检查规则与权限');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="r4-page r4-workspace">
      <Header title="会员标签与分群" subtitle="会员 / CDP / 标签与分群" />
      <MemberTabs pageId="ADM-F04" />
      <div className="r4-tag-workbench">
        <Panel
          title="会员标签"
          hint={`${tagRows.length} 个已使用标签`}
          toolbar={
            <div className="r4-maintenance-form" aria-label="会员打标">
              <label>
                目标会员
                <select value={memberId} onChange={(event) => setMemberId(event.target.value)}>
                  {members.map((member) => (
                    <option key={textOf(member, 'id')} value={textOf(member, 'id')}>
                      {profileName(member)} · {shortRef(member.id, '')}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                标签名称
                <input
                  value={tagName}
                  onChange={(event) => setTagName(event.target.value)}
                  placeholder="例如：高意向"
                />
              </label>
              <button
                className="primary"
                disabled={
                  busy ||
                  !memberId ||
                  !tagName.trim() ||
                  membersState.loading ||
                  Boolean(membersState.error)
                }
                onClick={() => void assignTag()}
              >
                保存会员标签
              </button>

              {tagMessage && (
                <small className="r4-help" role="status">
                  {tagMessage}
                </small>
              )}
              {Boolean(membersState.error) && (
                <p role="alert">
                  会员读取失败。<button onClick={membersState.reload}>重新加载</button>
                </p>
              )}
            </div>
          }
        >
          <State state={tagsState} count={tagRows.length} noun="会员标签">
            <div className="table-wrap r4-table">
              <table>
                <thead>
                  <tr>
                    <th>标签名称</th>
                    <th>类型</th>
                    <th>规则摘要</th>
                    <th>覆盖人数</th>
                    <th>状态</th>
                  </tr>
                </thead>
                <tbody>
                  {tagRows.map((tag) => (
                    <tr key={textOf(tag, 'id')}>
                      <td>
                        <span className="r4-tag-strong">{safeText(tag.name)}</span>
                      </td>
                      <td>{safeText(tag.source, '系统 / 运营')}</td>
                      <td>{safeText(tag.description, '来自会员标签分配事实')}</td>
                      <td>{Number(tag.memberCount ?? 0)}</td>
                      <td>
                        <Pill value="ENABLED" text="使用中" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </State>
        </Panel>
        <Panel
          title="分群规则构建器"
          hint={`${segmentRows.length} 个已保存分群`}
          toolbar={
            <div className="r4-maintenance-form" aria-label="创建分群">
              <label>
                分群名称
                <input
                  placeholder="输入分群名称"
                  value={segmentName}
                  onChange={(event) => setSegmentName(event.target.value)}
                />
              </label>
              <label>
                行为条件
                <select value={eventName} onChange={(event) => setEventName(event.target.value)}>
                  <option value="product_view">查看产品详情</option>
                  <option value="book_click">点击立即预订</option>
                  <option value="pay_invoke">调起支付</option>
                  <option value="pay_cancel_return">取消支付后返回</option>
                  <option value="pay_success_confirmed">服务端确认支付成功</option>
                </select>
              </label>
              <button
                className="primary"
                disabled={busy || !segmentName.trim()}
                onClick={() => void createSegment()}
              >
                保存并生成分群
              </button>
              {message && <small className="r4-help">{message}</small>}
            </div>
          }
        >
          <State state={segmentsState} count={segmentRows.length} noun="会员分群">
            <div className="table-wrap r4-table">
              <table>
                <thead>
                  <tr>
                    <th>分群名称</th>
                    <th>规则编号</th>
                    <th>命中会员</th>
                    <th>版本</th>
                    <th>状态</th>
                  </tr>
                </thead>
                <tbody>
                  {segmentRows.map((segment) => (
                    <tr key={textOf(segment, 'id')}>
                      <td>
                        <b>{safeText(segment.name)}</b>
                      </td>
                      <td>{safeText(segment.code)}</td>
                      <td>{Number(segment.memberCount ?? 0)}</td>
                      <td>V{Number(segment.version ?? 1)}</td>
                      <td>
                        <Pill value={segment.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </State>
        </Panel>
      </div>
    </div>
  );
}

function Funnel({ funnelState }: { funnelState: LoadState }) {
  const data = rows(funnelState.data);
  return (
    <div className="r4-page r4-workspace">
      <Header title="会员转化漏斗" subtitle="会员 / CDP / 转化漏斗" />
      <MemberTabs pageId="ADM-F05" />
      <Panel title="核心转化漏斗" hint="当前小程序 · 各阶段行为与首阶段比较">
        <State state={funnelState} count={data.length} noun="漏斗数据">
          <div className="table-wrap r4-table">
            <table>
              <thead>
                <tr>
                  <th>阶段</th>
                  <th>行为</th>
                  <th>事件量</th>
                  <th>首阶段转化率</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr key={`${textOf(item, 'eventType')}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{eventText[textOf(item, 'eventType')] ?? '转化阶段'}</td>
                    <td>{Number(item.count ?? 0).toLocaleString('zh-CN')}</td>
                    <td>
                      <div className="r4-conversion">
                        <span>{`${(Number(item.conversionBps ?? 0) / 100).toFixed(1)}%`}</span>
                        <i aria-hidden="true">
                          <b
                            style={{
                              width: `${Math.max(0, Math.min(100, Number(item.conversionBps ?? 0) / 100))}%`,
                            }}
                          />
                        </i>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </div>
  );
}

function Preferences({ preferencesState }: { preferencesState: LoadState }) {
  const summary = unwrapRecord(preferencesState.data);
  const cities = Array.isArray(summary.cities) ? summary.cities.filter(isRecord) : [];
  const products = Array.isArray(summary.products) ? summary.products.filter(isRecord) : [];
  const skus = Array.isArray(summary.skus) ? summary.skus.filter(isRecord) : [];
  return (
    <div className="r4-page r4-workspace">
      <Header title="会员偏好分析" subtitle="会员 / CDP　/　偏好分析" />
      <MemberTabs pageId="ADM-F06" />
      <Metrics
        items={[
          { label: '分析行为', value: Number(summary.eventCount ?? 0) },
          { label: '覆盖会员', value: Number(summary.memberCount ?? 0) },
          { label: '涉及城市', value: cities.length },
          { label: '涉及产品', value: products.length },
          { label: '涉及套餐', value: skus.length },
        ]}
      />
      <div className="r4-three">
        <Panel title="城市偏好">
          <State state={preferencesState} count={cities.length} noun="城市偏好">
            <ProgressList
              items={cities.map((row) => ({
                label: safeText(row.name),
                value: Number(row.count ?? 0),
              }))}
            />
          </State>
        </Panel>
        <Panel title="线路 / 主题偏好">
          <State state={preferencesState} count={products.length} noun="线路偏好">
            <ProgressList
              items={products.map((row) => ({
                label: safeText(row.name),
                value: Number(row.count ?? 0),
              }))}
            />
          </State>
        </Panel>
        <div>
          <Panel title="套餐偏好">
            <State state={preferencesState} count={skus.length} noun="套餐偏好">
              <ProgressList
                items={skus.map((row) => ({
                  label: safeText(row.name),
                  value: Number(row.count ?? 0),
                }))}
              />
            </State>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function Abandonment({ abandonmentState }: { abandonmentState: LoadState }) {
  const people = rows(abandonmentState.data);
  const [search, setSearch] = useState('');
  const visible = people.filter((person) =>
    `${safeText(person.nickname, '')} ${shortRef(person.memberId, '')}`
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );
  const cancelCount = people
    .filter((item) => textOf(item, 'latestEvent') === 'pay_cancel_return')
    .reduce((sum, item) => sum + Number(item.abandonmentCount ?? 0), 0);
  const failCount = people
    .filter((item) => textOf(item, 'latestEvent') === 'pay_fail_return')
    .reduce((sum, item) => sum + Number(item.abandonmentCount ?? 0), 0);
  return (
    <div className="r4-page r4-workspace">
      <Header title="支付放弃人群" subtitle="会员 / CDP　/　支付放弃人群" />
      <Notice warning>
        用户主动取消、余额不足等属于营销分析，不作为微信支付商户健康异常证据。
      </Notice>
      <MemberTabs pageId="ADM-F07" />
      <Metrics
        items={[
          { label: '支付放弃会员', value: people.length },
          { label: '用户取消返回', value: cancelCount },
          { label: '支付失败返回', value: failCount },
          {
            label: '待支付订单',
            value: people.reduce((sum, item) => sum + Number(item.pendingOrderCount ?? 0), 0),
          },
          { label: '商户健康影响', value: '不计入' },
        ]}
      />
      <div className="r4-side-layout wide-main">
        <Panel
          title="支付放弃人群"
          hint={`显示 ${visible.length} / ${people.length} 位会员`}
          toolbar={
            <Filters search={search} setSearch={setSearch} placeholder="查找会员昵称或编号" />
          }
        >
          <State state={abandonmentState} count={visible.length} noun="支付放弃人群">
            <div className="table-wrap r4-table">
              <table>
                <thead>
                  <tr>
                    <th>会员</th>
                    <th>最近放弃</th>
                    <th>放弃次数</th>
                    <th>待支付订单</th>
                    <th>挽回结果</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((person) => (
                    <tr key={textOf(person, 'memberId')}>
                      <td>
                        <NavLink href={pathFor('ADM-F02', { id: textOf(person, 'memberId') })}>
                          {safeText(person.nickname, shortRef(person.memberId, '会员'))}
                        </NavLink>
                      </td>
                      <td>{dateTime(person.latestAt)}</td>
                      <td>
                        <Pill value="OBSERVE" text={`${Number(person.abandonmentCount ?? 0)} 次`} />
                      </td>
                      <td>
                        {Number(person.pendingOrderCount ?? 0)} 单 /{' '}
                        {money(person.pendingAmountCents)}
                      </td>
                      <td>
                        曝光 {Number(person.rescueExposures ?? 0)} · 挽回{' '}
                        {Number(person.recoveredCount ?? 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </State>
        </Panel>
        <aside>
          <Panel title="原因分布">
            <ProgressList
              items={[
                {
                  label: '用户主动取消',
                  value: cancelCount,
                },
                {
                  label: '支付失败返回',
                  value: failCount,
                },
              ]}
            />
          </Panel>
          <Panel title="挽回策略效果">
            <ProgressList
              items={people.map((person) => ({
                label: safeText(person.nickname, '匿名会员'),
                value: Number(person.recoveredCount ?? 0),
              }))}
            />
          </Panel>
        </aside>
      </div>
    </div>
  );
}

function Privacy({
  api,
  appId,
  exportsState,
}: {
  api: AdminApiClient;
  appId: string;
  exportsState: LoadState;
}) {
  const jobs = rows(exportsState.data).filter((job) => textOf(job, 'dataset') === 'MEMBER_SUMMARY');
  const [busy, setBusy] = useState(false);
  const [exportFields, setExportFields] = useState(['memberNo', 'status', 'orderCount']);
  const [message, setMessage] = useState('');
  async function createExport() {
    if (busy || !exportFields.length) return;
    setBusy(true);
    setMessage('');
    try {
      await api.send(`/api/admin/m7/apps/${encodeURIComponent(appId)}/exports`, 'POST', {
        dataset: 'MEMBER_SUMMARY',
        format: 'XLSX',
        fields: exportFields,
        filters: {},
        idempotencyKey: `member-export-${Date.now()}`,
      });
      setMessage('会员导出任务已提交，生成完成后可申请短期下载授权');
      exportsState.reload();
    } catch {
      setMessage('提交失败：当前账号缺少 member.export 或 App 数据范围');
    } finally {
      setBusy(false);
    }
  }
  async function download(job: JsonRecord) {
    if (job.status !== 'READY') return;
    try {
      const jobId = encodeURIComponent(textOf(job, 'id'));
      const grant = unwrapRecord(
        await api.send(
          `/api/admin/m7/apps/${encodeURIComponent(appId)}/exports/${jobId}/download-grant`,
          'POST',
        ),
      );
      const blob = await api.download(
        `/api/admin/m7/apps/${encodeURIComponent(appId)}/exports/${jobId}/download?token=${encodeURIComponent(textOf(grant, 'token'))}`,
      );
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = safeText(job.fileName, 'member-summary.xlsx');
      anchor.click();
      URL.revokeObjectURL(url);
      setMessage('下载已开始，短期授权不会写入页面或日志');
    } catch {
      setMessage('下载失败，请刷新任务状态并重新申请授权');
    }
  }
  return (
    <div className="r4-page r4-workspace">
      <Header
        title="会员导出与隐私"
        subtitle="会员 / CDP / 会员导出与隐私"
        actions={<button onClick={exportsState.reload}>刷新任务状态</button>}
      />
      <MemberTabs pageId="ADM-F08" />
      <Panel title="创建会员导出任务" hint="当前小程序授权范围 · Excel 格式">
        <div className="r4-export-controls">
          <fieldset className="r4-field-choice">
            <legend>导出字段</legend>
            {[
              ['memberNo', '会员编号'],
              ['status', '会员状态'],
              ['orderCount', '订单数量'],
            ].map(([field, name]) => (
              <label key={field}>
                <input
                  type="checkbox"
                  checked={exportFields.includes(field!)}
                  disabled={busy}
                  onChange={(event) =>
                    setExportFields((current) =>
                      event.target.checked
                        ? [...current, field!]
                        : current.filter((value) => value !== field),
                    )
                  }
                />
                {name}
              </label>
            ))}
          </fieldset>
          <button
            className="primary"
            disabled={
              busy || !exportFields.length || exportsState.loading || Boolean(exportsState.error)
            }
            onClick={() => void createExport()}
          >
            {busy ? '提交中…' : '提交异步导出任务'}
          </button>
        </div>
        {message && (
          <p className="r4-help" role="status">
            {message}
          </p>
        )}
        <p className="r4-footnote">
          导出受账号权限与数据范围限制；文件生成后可下载，敏感身份及支付凭据不在导出范围内。
        </p>
      </Panel>
      <Panel title="导出任务记录" hint="下载链接到期后不可访问">
        <State state={exportsState} count={jobs.length} noun="会员导出任务">
          <div className="table-wrap r4-table">
            <table>
              <thead>
                <tr>
                  <th>任务</th>
                  <th>格式</th>
                  <th>行数</th>
                  <th>状态</th>
                  <th>创建时间</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={textOf(job, 'id')}>
                    <td>{shortRef(job.id, '导出')}</td>
                    <td>{safeText(job.format)}</td>
                    <td>{Number(job.rowCount ?? 0)}</td>
                    <td>
                      <Pill value={job.status} text={safeText(job.status)} />
                    </td>
                    <td>{dateTime(job.createdAt)}</td>
                    <td>
                      <button disabled={job.status !== 'READY'} onClick={() => void download(job)}>
                        下载
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </div>
  );
}

export function CanonicalMemberPage({ api, targetAppId, pageId, params }: PageProps) {
  const membersState = useApi(
    api,
    targetAppId ? `/api/admin/m5/members?appId=${encodeURIComponent(targetAppId)}` : undefined,
  );
  const members = rows(membersState.data);
  const firstMemberId = members[0] ? textOf(members[0], 'id') : '';
  const selectedId = params.id ?? firstMemberId;
  const detailState = useApi(
    api,
    targetAppId && selectedId && pageId === 'ADM-F02'
      ? `/api/admin/m5/members/${encodeURIComponent(selectedId)}?appId=${encodeURIComponent(targetAppId)}`
      : undefined,
  );
  const member =
    pageId === 'ADM-F02'
      ? unwrapRecord(detailState.data)
      : (members.find((item) => textOf(item, 'id') === selectedId) ?? {});
  const eventsState = useApi(
    api,
    targetAppId && selectedId && ['ADM-F02', 'ADM-F03'].includes(pageId)
      ? `/api/admin/m5/members/${encodeURIComponent(selectedId)}/events?appId=${encodeURIComponent(targetAppId)}`
      : undefined,
  );
  const funnelState = useApi(
    api,
    targetAppId && pageId === 'ADM-F05'
      ? `/api/admin/m5/behavior-funnel?appId=${encodeURIComponent(targetAppId)}`
      : undefined,
  );
  const tagsState = useApi(
    api,
    targetAppId && pageId === 'ADM-F04'
      ? `/api/admin/m5/tags?appId=${encodeURIComponent(targetAppId)}`
      : undefined,
  );
  const segmentsState = useApi(
    api,
    targetAppId && pageId === 'ADM-F04'
      ? `/api/admin/m5/segments?appId=${encodeURIComponent(targetAppId)}`
      : undefined,
  );
  const preferencesState = useApi(
    api,
    targetAppId && pageId === 'ADM-F06'
      ? `/api/admin/m5/preferences?appId=${encodeURIComponent(targetAppId)}`
      : undefined,
  );
  const abandonmentState = useApi(
    api,
    targetAppId && pageId === 'ADM-F07'
      ? `/api/admin/m5/payment-abandonment?appId=${encodeURIComponent(targetAppId)}`
      : undefined,
  );
  const exportsState = useApi(
    api,
    targetAppId && pageId === 'ADM-F08'
      ? `/api/admin/m7/apps/${encodeURIComponent(targetAppId)}/exports`
      : undefined,
  );
  if (!targetAppId) return <div className="r4-state">请先选择目标小程序。</div>;
  if (pageId === 'ADM-F01')
    return <MemberList pageId={pageId} membersState={membersState} appId={targetAppId} />;
  if (pageId === 'ADM-F02')
    return (
      <State state={detailState} count={Object.keys(member).length} noun="会员详情">
        <MemberDetail
          api={api}
          appId={targetAppId}
          member={member}
          eventsState={eventsState}
          reload={detailState.reload}
        />
      </State>
    );
  if (pageId === 'ADM-F03') return <EventTimeline member={member} eventsState={eventsState} />;
  if (pageId === 'ADM-F04')
    return (
      <TagSegments
        api={api}
        appId={targetAppId}
        membersState={membersState}
        tagsState={tagsState}
        segmentsState={segmentsState}
      />
    );
  if (pageId === 'ADM-F05') return <Funnel funnelState={funnelState} />;
  if (pageId === 'ADM-F06') return <Preferences preferencesState={preferencesState} />;
  if (pageId === 'ADM-F07') return <Abandonment abandonmentState={abandonmentState} />;
  return <Privacy api={api} appId={targetAppId} exportsState={exportsState} />;
}

const PaymentMenuContext = createContext<readonly string[]>([]);
function PaymentTabs({ pageId }: { pageId: string }) {
  const allowedMenus = useContext(PaymentMenuContext);
  const tabs: Array<[string, string]> = [
    ['ADM-G01', '支付商户'],
    ['ADM-G03', '绑定矩阵'],
    ['ADM-G04', '路由策略'],
    ['ADM-G05', '健康监控'],
    ['ADM-G07', '交易流水'],
    ['ADM-G08', '失败分析'],
    ['ADM-G09', '退款流水'],
    ['ADM-G10', '自动保护'],
  ];
  return (
    <nav className="r4-tabs">
      {tabs
        .filter(([id]) => allowedMenus.includes(id))
        .map(([id, text]) => (
          <a key={id} className={pageId === id ? 'active' : ''} href={pathFor(id)}>
            {text}
          </a>
        ))}
    </nav>
  );
}

function merchantOf(record: JsonRecord) {
  return nested(record, 'merchant');
}

const pendingMerchantKey = (appId: string) =>
  `travel.admin.pendingMerchantId.${encodeURIComponent(appId)}`;

function merchantName(record: JsonRecord) {
  const merchant = merchantOf(record);
  return safeText(record.displayName ?? merchant.displayName, '未命名商户');
}

function mchid(record: JsonRecord) {
  const merchant = merchantOf(record);
  return safeText(record.mchid ?? merchant.mchid, '暂未记录');
}

export function AppPaymentPoolPanel({
  api,
  appId,
  appName,
  embedded = false,
}: {
  api: AdminApiClient;
  appId: string;
  appName: string;
  embedded?: boolean;
}) {
  const merchantOptionsState = useApi(
    api,
    `/api/admin/m3/apps/${encodeURIComponent(appId)}/payment-merchant-options`,
  );
  const bindingsState = useApi(
    api,
    `/api/admin/m3/apps/${encodeURIComponent(appId)}/payment-bindings`,
  );
  const merchants = rows(merchantOptionsState.data);
  const bindings = rows(bindingsState.data);
  const linkedMerchantIds = bindings
    .filter((binding) => textOf(binding, 'status') !== 'DISABLED')
    .map((binding) => textOf(binding, 'merchantId'));
  const [selectedMerchantIds, setSelectedMerchantIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [merchantSearch, setMerchantSearch] = useState('');
  const selectionUnavailable =
    busy ||
    bindingsState.loading ||
    merchantOptionsState.loading ||
    Boolean(bindingsState.error || merchantOptionsState.error);
  useEffect(() => setSelectedMerchantIds(linkedMerchantIds), [appId, bindingsState.data]);

  const bindingByMerchantId = new Map(
    bindings.map((binding) => [textOf(binding, 'merchantId'), binding]),
  );
  const save = async () => {
    setBusy(true);
    setMessage('');
    try {
      await api.send(`/api/admin/m3/apps/${encodeURIComponent(appId)}/payment-bindings`, 'PUT', {
        merchantIds: selectedMerchantIds,
      });
      setMessage('绑定池已保存。新加入的商户将等待微信授权核验，移出的商户已退出调度。');
      bindingsState.reload();
      merchantOptionsState.reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '绑定池保存失败，请刷新后重试。');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`r4-page r4-workspace ${embedded ? 'r4-page--embedded' : ''}`}>
      {!embedded && (
        <>
          <Header
            title="收款商户绑定池"
            subtitle={`${appName} / 支付商户多对多绑定 · 仅已核验且可用的关系才会进入收款候选池`}
            actions={
              <button
                onClick={() => {
                  merchantOptionsState.reload();
                  bindingsState.reload();
                }}
              >
                刷新数据
              </button>
            }
          />
          <Notice>
            在此可一次选择多个支付商户号。保存后两端关系同步：新增关系保持“待验证”，取消选择会停止该关系的收款调度并保留支付审计记录。
          </Notice>
          <Metrics
            items={[
              { label: '可配置商户', value: merchants.length },
              { label: '已关联商户', value: bindings.length },
              {
                label: '可收款',
                value: bindings.filter((binding) => textOf(binding, 'status') === 'ENABLED').length,
              },
              {
                label: '待微信核验',
                value: bindings.filter((binding) => textOf(binding, 'status') === 'PENDING_VERIFY')
                  .length,
              },
              {
                label: '已退出调度',
                value: bindings.filter((binding) => textOf(binding, 'status') === 'DISABLED')
                  .length,
              },
            ]}
          />
        </>
      )}
      <Panel
        title="选择支付商户号"
        hint="可同时关联多个商户号；仅微信双向核验成功的关系会进入收款候选池。"
      >
        <div className="r4-form-grid">
          {embedded ? (
            <div className="app-merchant-picker">
              <label>
                查找商户
                <input
                  type="search"
                  value={merchantSearch}
                  onChange={(event) => setMerchantSearch(event.target.value)}
                  placeholder="输入商户名称或商户号"
                />
              </label>
              <p className="app-config-hint" aria-live="polite">
                已选择 {selectedMerchantIds.length} 个商户，保存后同步绑定关系。
              </p>
              {merchantOptionsState.error || bindingsState.error ? (
                <p role="alert">商户信息读取失败，请刷新数据后重试。</p>
              ) : null}
              <div className="app-merchant-options" role="group" aria-label="支付商户号多选">
                {merchants
                  .filter((merchant) =>
                    `${merchantName(merchant)} ${mchid(merchant)}`
                      .toLowerCase()
                      .includes(merchantSearch.trim().toLowerCase()),
                  )
                  .map((merchant) => {
                    const id = textOf(merchant, 'id');
                    const selected = selectedMerchantIds.includes(id);
                    const binding = bindingByMerchantId.get(id);
                    return (
                      <label
                        key={id}
                        className={`app-merchant-option ${selected ? 'is-selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={selectionUnavailable}
                          onChange={(event) =>
                            setSelectedMerchantIds((current) =>
                              event.target.checked
                                ? [...current, id]
                                : current.filter((item) => item !== id),
                            )
                          }
                        />
                        <span>
                          <b>{merchantName(merchant)}</b>
                          <small>{mchid(merchant)}</small>
                        </span>
                        <Pill value={binding?.status ?? 'PENDING_VERIFY'} />
                      </label>
                    );
                  })}
              </div>
              {!merchantOptionsState.loading &&
                !merchants.length &&
                !merchantOptionsState.error && (
                  <p className="app-config-hint">暂无可关联商户，请先在支付中心添加支付商户号。</p>
                )}
            </div>
          ) : (
            <label className="r4-multi-picker">
              支付商户号（可多选）
              <select
                aria-label="支付商户号多选"
                disabled={selectionUnavailable}
                multiple
                size={Math.min(Math.max(merchants.length, 4), 8)}
                value={selectedMerchantIds}
                onChange={(event) =>
                  setSelectedMerchantIds(
                    Array.from(event.currentTarget.selectedOptions, (item) => item.value),
                  )
                }
              >
                {merchants.map((merchant) => {
                  const existing = bindingByMerchantId.get(textOf(merchant, 'id'));
                  return (
                    <option key={textOf(merchant, 'id')} value={textOf(merchant, 'id')}>
                      {merchantName(merchant)} · {mchid(merchant)}
                      {existing ? `（${label(existing.status)}）` : '（新增后待验证）'}
                    </option>
                  );
                })}
              </select>
            </label>
          )}
          <div className="r4-binding-actions">
            <button
              className="button"
              disabled={selectionUnavailable}
              type="button"
              onClick={() => setSelectedMerchantIds(merchants.map((item) => textOf(item, 'id')))}
            >
              全选可用商户
            </button>
            <button
              className="button"
              disabled={selectionUnavailable}
              type="button"
              onClick={() => setSelectedMerchantIds([])}
            >
              清空选择
            </button>
            <button
              className="primary"
              disabled={selectionUnavailable}
              type="button"
              onClick={() => void save()}
            >
              {busy ? '正在保存…' : '保存绑定池'}
            </button>
            {message && <small className="r4-help">{message}</small>}
          </div>
        </div>
      </Panel>
      <Panel title="当前绑定关系" hint="未完成微信双向授权的关系不会被支付路由选中。">
        <State state={bindingsState} count={bindings.length} noun="支付绑定">
          <div className="table-wrap r4-table">
            <table>
              <thead>
                <tr>
                  <th>支付商户号</th>
                  <th>微信授权状态</th>
                  <th>当前调度状态</th>
                  <th>处理说明</th>
                </tr>
              </thead>
              <tbody>
                {bindings.map((binding) => {
                  const merchant = merchantOf(binding);
                  const status = textOf(binding, 'status');
                  return (
                    <tr key={textOf(binding, 'id')}>
                      <td>
                        <b>{safeText(merchant.mchid)}</b>
                        <small className="r4-sub">{safeText(merchant.displayName)}</small>
                      </td>
                      <td>{binding.verifiedAt ? '已由服务端核验' : '等待微信授权核验'}</td>
                      <td>
                        <Pill value={status} />
                      </td>
                      <td>
                        {status === 'ENABLED'
                          ? '纳入健康收款候选池'
                          : status === 'PENDING_VERIFY'
                            ? '完成微信后台双向授权后可核验'
                            : '已保留历史，但不会参与新收款'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </div>
  );
}

function MerchantCreate({
  api,
  appId,
  onDone,
  onClose,
}: {
  api: AdminApiClient;
  appId: string;
  onDone: () => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [number, setNumber] = useState('');
  const [credential, setCredential] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  async function submit() {
    setBusy(true);
    setResult('');
    try {
      const response = await api.send<unknown>(
        `/api/admin/m3/apps/${appId}/payment-merchants`,
        'POST',
        { displayName: name.trim(), mchid: number.trim(), credentialSecretName: credential.trim() },
      );
      const created = unwrapRecord(response);
      if (typeof created.id === 'string')
        sessionStorage.setItem(pendingMerchantKey(appId), created.id);
      setResult('商户已创建，可继续建立小程序绑定。');
      setName('');
      setNumber('');
      setCredential('');
      onDone();
    } catch {
      setResult('创建未完成，请检查商户资料、权限或安全凭据引用。');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Panel title="新增支付商户" hint="填写收款主体与已托管的凭据引用" className="r4-command">
      <div className="r4-form-grid">
        <label>
          商户显示名称
          <input
            aria-label="商户显示名称"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="例如：云南旅游服务主体"
          />
        </label>
        <label>
          微信支付商户号
          <input
            aria-label="微信支付商户号"
            value={number}
            onChange={(event) => setNumber(event.target.value)}
            inputMode="numeric"
          />
        </label>
        <label>
          安全凭据引用
          <input
            aria-label="安全凭据引用"
            type="password"
            value={credential}
            onChange={(event) => setCredential(event.target.value)}
            placeholder="填写已配置的安全引用名"
          />
          <small>不是 APIv3 Key 或私钥明文</small>
        </label>
      </div>
      <div className="button-row">
        <button
          className="primary"
          disabled={busy || !name.trim() || !number.trim() || !credential.trim()}
          onClick={() => void submit()}
        >
          {busy ? '提交中…' : '创建商户'}
        </button>
        <button disabled={busy} onClick={onClose}>
          取消
        </button>
        {result && <span role="status">{result}</span>}
      </div>
    </Panel>
  );
}

function PaymentMerchantList({
  api,
  appId,
  merchantsState,
  bindingsState,
  healthState,
}: {
  api: AdminApiClient;
  appId: string;
  merchantsState: LoadState;
  bindingsState: LoadState;
  healthState: LoadState;
}) {
  const merchants = rows(merchantsState.data);
  const bindings = rows(bindingsState.data);
  const health = rows(healthState.data);
  const aggregate = appId === AUTHORIZED_SCOPE;
  const configurationAppId = configurationScopeAppId(api, appId);
  const bindingsAvailable = !bindingsState.loading && !bindingsState.error;
  const healthAvailable = !healthState.loading && !healthState.error;
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdMessage, setCreatedMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const visible = merchants.filter(
    (merchant) =>
      `${merchantName(merchant)} ${mchid(merchant)}`.toLowerCase().includes(search.toLowerCase()) &&
      (statusFilter === 'ALL' || textOf(merchant, 'status') === statusFilter),
  );
  return (
    <div className="r4-page r4-workspace">
      <Header
        title="支付商户号列表"
        subtitle="支付中心　/　支付商户号列表"
        actions={
          <button
            className="primary"
            disabled={aggregate && !configurationAppId}
            aria-expanded={creating}
            onClick={() => setCreating(true)}
          >
            + 新增商户号
          </button>
        }
      />
      {aggregate && (
        <Notice>
          商户与绑定按当前授权范围读取；数量仅统计本页记录。
          {!configurationAppId ? '新增商户须在本页筛选区选择操作小程序。' : ''}
        </Notice>
      )}
      {Boolean(bindingsState.error || healthState.error) && (
        <Notice warning>
          关联绑定或健康数据读取失败，相关数量暂不可用，不影响已成功读取的商户名单。
          <button
            type="button"
            onClick={() => {
              bindingsState.reload();
              healthState.reload();
            }}
          >
            重试关联数据
          </button>
        </Notice>
      )}
      {createdMessage && (
        <div className="r4-inline-status" role="status">
          {createdMessage} <NavLink href={pathFor('ADM-G03')}>设置小程序绑定</NavLink>
        </div>
      )}
      {creating && (
        <MerchantCreate
          api={api}
          appId={configurationAppId || appId}
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            setCreatedMessage('商户已创建，请继续设置小程序绑定。');
            merchantsState.reload();
          }}
        />
      )}
      <Metrics
        items={[
          { label: aggregate ? '本页商户号' : '当前商户号', value: merchants.length },
          {
            label: '正常可用',
            value: merchants.filter((item) => textOf(item, 'status') === 'ENABLED').length,
          },
          {
            label: '待验证绑定',
            value: bindingsAvailable
              ? bindings.filter((item) => textOf(item, 'status') === 'PENDING_VERIFY').length
              : '暂不可用',
          },
          {
            label: '已降级绑定',
            value: bindingsAvailable
              ? bindings.filter((item) => textOf(item, 'status') === 'DEGRADED').length
              : '暂不可用',
          },
          { label: '健康快照', value: healthAvailable ? health.length : '暂不可用' },
        ]}
      />
      <PaymentTabs pageId="ADM-G01" />
      <Panel
        title="支付商户号"
        hint={`显示 ${visible.length} / ${merchants.length} 个商户`}
        toolbar={
          <Filters search={search} setSearch={setSearch} placeholder="商户名称或商户号">
            <select
              aria-label="商户状态"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="ALL">全部状态</option>
              {Array.from(new Set(merchants.map((merchant) => textOf(merchant, 'status')))).map(
                (status) => (
                  <option key={status} value={status}>
                    {label(status)}
                  </option>
                ),
              )}
            </select>
          </Filters>
        }
      >
        <State state={merchantsState} count={visible.length} noun="支付商户">
          <div className="table-wrap r4-table">
            <table>
              <thead>
                <tr>
                  <th>MchID / 主体</th>
                  <th>当前状态</th>
                  <th>合法绑定</th>
                  <th>健康分</th>
                  <th>敏感配置</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((merchant) => {
                  const id = textOf(merchant, 'id');
                  const ownBindings = bindings.filter(
                    (binding) => textOf(binding, 'merchantId') === id,
                  );
                  const snapshot = health.find((item) => textOf(item, 'merchantId') === id);
                  return (
                    <tr key={id}>
                      <td>
                        <strong>{mchid(merchant)}</strong>
                        <small className="r4-sub">{merchantName(merchant)}</small>
                      </td>
                      <td>
                        <Pill value={textOf(merchant, 'status')} />
                      </td>
                      <td>
                        {bindingsAvailable
                          ? `${ownBindings.length} 个小程序绑定`
                          : '绑定数据暂不可用'}
                      </td>
                      <td>
                        {!healthAvailable
                          ? '健康数据暂不可用'
                          : snapshot
                            ? safeText(snapshot.healthScore)
                            : '暂无快照'}
                      </td>
                      <td>
                        <Pill value="ENABLED" text="服务端安全托管" />
                      </td>
                      <td>
                        <a href={pathFor('ADM-G02', { id })}>详情</a>{' '}
                        <a href={`${pathFor('ADM-G02', { id })}#merchant-app-bindings`}>
                          关联小程序
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </div>
  );
}

function MerchantAppBindingManager({
  api,
  anchorAppId,
  merchantId,
  appsState,
  merchantBindingsState,
}: {
  api: AdminApiClient;
  anchorAppId: string;
  merchantId: string;
  appsState: LoadState;
  merchantBindingsState: LoadState;
}) {
  const apps = rows(appsState.data);
  const bindings = rows(merchantBindingsState.data);
  const linkedAppIds = bindings
    .filter((binding) => textOf(binding, 'status') !== 'DISABLED')
    .map((binding) => textOf(binding, 'appId'));
  const [selectedAppIds, setSelectedAppIds] = useState<string[]>([]);
  const [appSearch, setAppSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const selectionUnavailable =
    busy ||
    appsState.loading ||
    merchantBindingsState.loading ||
    Boolean(appsState.error || merchantBindingsState.error);
  useEffect(() => setSelectedAppIds(linkedAppIds), [merchantId, merchantBindingsState.data]);
  const bindingByAppId = new Map(bindings.map((binding) => [textOf(binding, 'appId'), binding]));
  const save = async () => {
    setBusy(true);
    setMessage('');
    try {
      await api.send(
        `/api/admin/m3/payment-merchants/${encodeURIComponent(merchantId)}/app-bindings`,
        'PUT',
        { appIds: selectedAppIds },
      );
      setMessage('小程序绑定已保存；新增关系等待微信双向授权核验。');
      merchantBindingsState.reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '绑定保存失败，请刷新后重试。');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section id="merchant-app-bindings" aria-label="关联小程序">
      <Panel
        title="关联小程序"
        hint={`已选 ${selectedAppIds.length} 个；新增关联须通过微信授权核验`}
        toolbar={
          <Filters
            search={appSearch}
            setSearch={setAppSearch}
            placeholder="查找小程序名称或业务编码"
          />
        }
      >
        {Boolean(appsState.error || merchantBindingsState.error) && (
          <div className="r4-inline-status error" role="alert">
            小程序或绑定信息读取失败，请刷新后重试。
            <button
              onClick={() => {
                appsState.reload();
                merchantBindingsState.reload();
              }}
            >
              重新加载
            </button>
          </div>
        )}
        <div className="r4-selection-grid" role="group" aria-label="关联小程序多选">
          {apps
            .filter((app) =>
              `${safeText(app.displayName)} ${safeText(app.appKey)}`
                .toLowerCase()
                .includes(appSearch.trim().toLowerCase()),
            )
            .map((app) => {
              const id = textOf(app, 'id');
              const binding = bindingByAppId.get(id);
              return (
                <label key={id} className={selectedAppIds.includes(id) ? 'is-selected' : ''}>
                  <input
                    type="checkbox"
                    aria-label={`关联 ${safeText(app.displayName)}`}
                    checked={selectedAppIds.includes(id)}
                    disabled={selectionUnavailable}
                    onChange={(event) =>
                      setSelectedAppIds((current) =>
                        event.target.checked
                          ? [...current, id]
                          : current.filter((value) => value !== id),
                      )
                    }
                  />
                  <span>
                    <strong>{safeText(app.displayName)}</strong>
                    <small>{safeText(app.appKey)}</small>
                  </span>
                  {binding ? <Pill value={binding.status} /> : <small>未关联</small>}
                </label>
              );
            })}
        </div>
        {!appsState.loading && !appsState.error && !apps.length && (
          <div className="r4-state">暂无可关联小程序</div>
        )}
        <div className="r4-toolbar-actions">
          <div className="r4-binding-actions">
            <button
              className="button"
              type="button"
              disabled={selectionUnavailable}
              onClick={() => setSelectedAppIds(apps.map((item) => textOf(item, 'id')))}
            >
              全选小程序
            </button>
            <button
              className="button"
              type="button"
              disabled={selectionUnavailable}
              onClick={() => setSelectedAppIds([])}
            >
              清空选择
            </button>
            <button
              className="primary"
              disabled={selectionUnavailable}
              type="button"
              onClick={() => void save()}
            >
              {busy ? '正在保存…' : '保存关联小程序'}
            </button>
            {message && (
              <small className="r4-help" role="status">
                {message}
              </small>
            )}
          </div>
        </div>
        <div className="r4-binding-summary">
          {anchorAppId === ''
            ? '请先选择当前小程序。'
            : `已保存 ${linkedAppIds.length} 个关联。取消勾选后保存，该关联停止新收款并保留历史记录。`}
        </div>
      </Panel>
    </section>
  );
}

function MerchantDetail({
  api,
  appId,
  merchant,
  onReload,
  appsState,
  merchantBindingsState,
  healthState,
}: {
  api: AdminApiClient;
  appId: string;
  merchant: JsonRecord;
  onReload: () => void;
  appsState: LoadState;
  merchantBindingsState: LoadState;
  healthState: LoadState;
}) {
  const bindings = rows(merchantBindingsState.data);
  const health =
    rows(healthState.data).find((item) => textOf(item, 'merchantId') === textOf(merchant, 'id')) ??
    {};
  const [showCredentialForm, setShowCredentialForm] = useState(false);
  const [credentialSecretName, setCredentialSecretName] = useState('');
  const [credentialBusy, setCredentialBusy] = useState(false);
  const [credentialMessage, setCredentialMessage] = useState('');
  return (
    <div className="r4-page r4-workspace">
      <Header
        title="支付商户号详情"
        subtitle="支付中心　/　支付商户号详情"
        actions={
          <a className="r3-button-link" href={pathFor('ADM-G03')}>
            查看绑定矩阵
          </a>
        }
      />
      <div className="r4-three merchant-detail">
        <Panel title="商户基础资料" hint={`当前状态：${label(textOf(merchant, 'status'))}`}>
          <div className="r4-merchant-title">
            <b>支</b>
            <div>
              <h2>MchID {mchid(merchant)}</h2>
              <p>{merchantName(merchant)}</p>
            </div>
          </div>
          <dl className="r4-kv">
            <div>
              <dt>商户号</dt>
              <dd>{mchid(merchant)}</dd>
            </div>
            <div>
              <dt>主体名称</dt>
              <dd>{merchantName(merchant)}</dd>
            </div>
            <div>
              <dt>创建时间</dt>
              <dd>{dateTime(merchant.createdAt)}</dd>
            </div>
            <div>
              <dt>配置版本</dt>
              <dd>第 {safeText(merchant.version, '1')} 版</dd>
            </div>
          </dl>
        </Panel>
        <Panel title="健康与交易摘要">
          <div className="r4-health-score">
            <strong>{safeText(health.healthScore, '—')}</strong>
            <span>当前健康分</span>
          </div>
          <Metrics
            items={[
              { label: '系统失败', value: safeText(health.systemFailureCount, '0') },
              { label: '排除用户事件', value: safeText(health.excludedUserCount, '0') },
            ]}
          />
        </Panel>
        <Panel title="安全操作">
          <Notice>敏感凭据仅能由具备高风险权限的人员通过受控流程更新，并留下完整审计。</Notice>
          <a className="r4-button-block" href={pathFor('ADM-G05')}>
            查看健康监控
          </a>
          <button onClick={() => setShowCredentialForm((visible) => !visible)}>
            {showCredentialForm ? '取消更新' : '更新安全凭据'}
          </button>
          {showCredentialForm && (
            <div className="r4-inline-form">
              <label>
                Secrets Provider 密钥名称
                <input
                  type="password"
                  autoComplete="off"
                  value={credentialSecretName}
                  placeholder="只提交密钥引用，不填私钥明文"
                  onChange={(event) => setCredentialSecretName(event.target.value)}
                />
              </label>
              <button
                className="primary"
                disabled={credentialBusy || !credentialSecretName.trim()}
                onClick={() => {
                  setCredentialBusy(true);
                  setCredentialMessage('');
                  void api
                    .send(
                      `/api/admin/m3/apps/${encodeURIComponent(appId)}/payment-merchants/${encodeURIComponent(textOf(merchant, 'id'))}/credential`,
                      'PATCH',
                      {
                        credentialSecretName: credentialSecretName.trim(),
                        expectedVersion: Number(merchant.version ?? 0),
                      },
                    )
                    .then(() => {
                      setCredentialSecretName('');
                      setShowCredentialForm(false);
                      setCredentialMessage('密钥引用已轮换，已写入高风险审计日志');
                      onReload();
                    })
                    .catch((error: unknown) =>
                      setCredentialMessage(error instanceof Error ? error.message : '凭据轮换失败'),
                    )
                    .finally(() => setCredentialBusy(false));
                }}
              >
                {credentialBusy ? '验证并更新中…' : '确认轮换引用'}
              </button>
            </div>
          )}
          {credentialMessage && <small className="r4-help">{credentialMessage}</small>}
        </Panel>
      </div>
      <MerchantAppBindingManager
        api={api}
        anchorAppId={appId}
        merchantId={textOf(merchant, 'id')}
        appsState={appsState}
        merchantBindingsState={merchantBindingsState}
      />
      <Panel title="AppID 合法绑定关系" hint="只有验证通过且未停用的组合可进入候选池">
        <State state={merchantBindingsState} count={bindings.length} noun="合法绑定">
          <div className="table-wrap r4-table">
            <table>
              <thead>
                <tr>
                  <th>小程序</th>
                  <th>绑定状态</th>
                  <th>验证结果</th>
                  <th>路由资格</th>
                  <th>最近更新</th>
                </tr>
              </thead>
              <tbody>
                {bindings.map((binding, index) => (
                  <tr key={textOf(binding, 'id')}>
                    <td>
                      <b>{safeText(nested(binding, 'app').displayName, `小程序 ${index + 1}`)}</b>
                      <small className="r4-sub">{safeText(nested(binding, 'app').appKey)}</small>
                    </td>
                    <td>
                      <Pill value={textOf(binding, 'status')} />
                    </td>
                    <td>
                      {binding.verifiedAt
                        ? `已于 ${dateTime(binding.verifiedAt)} 验证`
                        : '等待服务端验证'}
                    </td>
                    <td>
                      {['ENABLED', 'DEGRADED'].includes(textOf(binding, 'status'))
                        ? '可参与候选'
                        : '不参与路由'}
                    </td>
                    <td>{dateTime(binding.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </div>
  );
}

function BindingManager({
  api,
  appId,
  bindingsState,
  merchantsState,
}: {
  api: AdminApiClient;
  appId: string;
  bindingsState: LoadState;
  merchantsState: LoadState;
}) {
  const bindings = rows(bindingsState.data);
  const merchants = rows(merchantsState.data);
  const [merchantId, setMerchantId] = useState(
    () => sessionStorage.getItem(pendingMerchantKey(appId)) ?? '',
  );
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  useEffect(() => setMerchantId(sessionStorage.getItem(pendingMerchantKey(appId)) ?? ''), [appId]);
  async function send(path: string, method: 'POST' | 'PUT', body: unknown, clearPending = false) {
    setBusy(true);
    setResult('');
    try {
      await api.send(path, method, body);
      if (clearPending) sessionStorage.removeItem(pendingMerchantKey(appId));
      setResult('操作已完成并记录审计。');
      bindingsState.reload();
    } catch {
      setResult('操作未完成，请检查权限、版本或微信授权状态。');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="r4-page r4-workspace">
      <Header title="AppID 与 MchID 绑定矩阵" subtitle="支付中心　/　AppID 与 MchID 绑定矩阵" />
      <Notice>绑定关系是支付路由的第一道硬门槛：未授权、未验证、已停用的组合不会被选中。</Notice>
      <Metrics
        items={[
          { label: '当前小程序', value: 1 },
          { label: '可选商户号', value: merchants.length },
          {
            label: '有效绑定',
            value: bindings.filter((item) =>
              ['ENABLED', 'DEGRADED'].includes(textOf(item, 'status')),
            ).length,
          },
          {
            label: '待验证',
            value: bindings.filter((item) => textOf(item, 'status') === 'PENDING_VERIFY').length,
          },
          {
            label: '已停用',
            value: bindings.filter((item) => textOf(item, 'status') === 'DISABLED').length,
          },
        ]}
      />
      <PaymentTabs pageId="ADM-G03" />
      <Panel title="新增合法绑定" hint="先创建，再由服务端适配器验证">
        <div className="r4-inline-form">
          <label>
            选择支付商户
            <select value={merchantId} onChange={(event) => setMerchantId(event.target.value)}>
              <option value="">请选择商户</option>
              {merchantId &&
                !merchants.some((merchant) => textOf(merchant, 'id') === merchantId) && (
                  <option value={merchantId}>刚创建的商户（等待列表刷新）</option>
                )}
              {merchants.map((merchant) => (
                <option key={textOf(merchant, 'id')} value={textOf(merchant, 'id')}>
                  {merchantName(merchant)} · {mchid(merchant)}
                </option>
              ))}
            </select>
          </label>
          <button
            className="primary"
            disabled={busy || !merchantId}
            onClick={() =>
              void send(
                `/api/admin/m3/apps/${appId}/payment-bindings`,
                'POST',
                { merchantId },
                true,
              )
            }
          >
            创建待验证绑定
          </button>
          {result && <span>{result}</span>}
        </div>
      </Panel>
      <Panel title="AppID × MchID 绑定矩阵" hint="点击操作完成验证、停用或审计恢复">
        <State state={bindingsState} count={bindings.length} noun="支付绑定">
          <div className="table-wrap r4-table">
            <table>
              <thead>
                <tr>
                  <th>小程序</th>
                  <th>支付商户</th>
                  <th>绑定状态</th>
                  <th>验证状态</th>
                  <th>路由资格</th>
                  <th>受控操作</th>
                </tr>
              </thead>
              <tbody>
                {bindings.map((binding) => {
                  const merchant = merchantOf(binding);
                  const status = textOf(binding, 'status');
                  const version = Number(binding.version ?? 0);
                  return (
                    <tr key={textOf(binding, 'id')}>
                      <td>当前目标小程序</td>
                      <td>
                        <strong>{safeText(merchant.mchid)}</strong>
                        <small className="r4-sub">{safeText(merchant.displayName)}</small>
                      </td>
                      <td>
                        <Pill value={status} />
                      </td>
                      <td>{binding.verifiedAt ? '服务端验证通过' : '尚未完成验证'}</td>
                      <td>
                        {['ENABLED', 'DEGRADED'].includes(status) ? '可参与候选' : '不参与路由'}
                      </td>
                      <td>
                        {status === 'PENDING_VERIFY' ? (
                          <button
                            disabled={busy}
                            onClick={() =>
                              void send(
                                `/api/admin/m3/apps/${appId}/payment-bindings/${textOf(binding, 'id')}/verify`,
                                'POST',
                                { expectedVersion: version },
                              )
                            }
                          >
                            验证授权
                          </button>
                        ) : (
                          <button
                            disabled={busy}
                            onClick={() =>
                              void send(
                                `/api/admin/m3/apps/${appId}/payment-bindings/${textOf(binding, 'id')}/status`,
                                'PUT',
                                {
                                  status: status === 'DISABLED' ? 'ENABLED' : 'DISABLED',
                                  expectedVersion: version,
                                  reasonCode: 'OPERATOR_CONFIRMED',
                                },
                              )
                            }
                          >
                            {status === 'DISABLED' ? '审计恢复' : '停用绑定'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </div>
  );
}

function RouteRules({
  api,
  appId,
  rulesState,
  bindingsState,
}: {
  api: AdminApiClient;
  appId: string;
  rulesState: LoadState;
  bindingsState: LoadState;
}) {
  const rules = rows(rulesState.data);
  const bindings = rows(bindingsState.data);
  const [bindingId, setBindingId] = useState('');
  const [search, setSearch] = useState('');
  const selected = rules.find((item) => textOf(item, 'bindingId') === bindingId);
  const selectedBinding = bindings.find((item) => textOf(item, 'id') === bindingId);
  const [priority, setPriority] = useState('100');
  const [weight, setWeight] = useState('100');
  const [ruleEnabled, setRuleEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  const unavailable =
    busy ||
    rulesState.loading ||
    bindingsState.loading ||
    Boolean(rulesState.error || bindingsState.error);
  const eligible =
    selectedBinding && ['ENABLED', 'DEGRADED'].includes(textOf(selectedBinding, 'status'));
  const validNumbers =
    priority.trim() !== '' &&
    weight.trim() !== '' &&
    Number.isSafeInteger(Number(priority)) &&
    Number.isSafeInteger(Number(weight)) &&
    Number(weight) > 0;
  useEffect(() => {
    setPriority(String(selected?.priority ?? 100));
    setWeight(String(selected?.weight ?? 100));
    setRuleEnabled(selected?.enabled !== false);
  }, [bindingId, selected]);
  useEffect(() => setResult(''), [bindingId]);
  useEffect(() => setBindingId(''), [appId]);
  function selectBinding(id: string) {
    if (unavailable || id === bindingId) return;
    const rule = rules.find((item) => textOf(item, 'bindingId') === id);
    // Commit the binding and its fields together so the editor never exposes another rule's values.
    setPriority(String(rule?.priority ?? 100));
    setWeight(String(rule?.weight ?? 100));
    setRuleEnabled(rule?.enabled !== false);
    setResult('');
    setBindingId(id);
  }
  const visible = bindings.filter((binding) =>
    `${merchantName(binding)} ${mchid(binding)}`.toLowerCase().includes(search.toLowerCase()),
  );
  async function save() {
    if (unavailable || !eligible || !validNumbers) return;
    setBusy(true);
    setResult('');
    try {
      await api.send(`/api/admin/m3/apps/${appId}/payment-route-rules`, 'PUT', {
        bindingId,
        ruleCode:
          textOf(selected ?? {}, 'ruleCode') === '—'
            ? 'DEFAULT'
            : textOf(selected ?? {}, 'ruleCode'),
        priority: Number(priority),
        weight: Number(weight),
        enabled: ruleEnabled,
        expectedVersion: Number(selected?.version ?? 0),
        conditions: selected?.conditions ?? {},
      });
      setResult('路由规则已保存。');
      rulesState.reload();
    } catch {
      setResult('保存失败，请检查绑定资格或版本冲突。输入内容已保留。');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="r4-page r4-workspace">
      <Header title="支付路由策略" subtitle="支付中心 / 支付路由策略" />
      <PaymentTabs pageId="ADM-G04" />
      <Panel
        title="路由策略：当前生效策略"
        hint="选择商户维护优先级与权重"
        toolbar={<Filters search={search} setSearch={setSearch} placeholder="查找支付商户" />}
      >
        <div className="r4-route-workbench">
          <State state={bindingsState} count={visible.length} noun="支付绑定">
            <div className="table-wrap r4-table">
              <table>
                <thead>
                  <tr>
                    <th>支付商户</th>
                    <th>绑定状态</th>
                    <th>策略</th>
                    <th>优先级</th>
                    <th>权重</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((binding) => {
                    const id = textOf(binding, 'id');
                    const rule = rules.find((item) => textOf(item, 'bindingId') === id);
                    const qualified = ['ENABLED', 'DEGRADED'].includes(textOf(binding, 'status'));
                    return (
                      <tr key={id} className={bindingId === id ? 'is-selected' : ''}>
                        <td>
                          <strong>{merchantName(binding)}</strong>
                          <small className="r4-sub">{mchid(binding)}</small>
                        </td>
                        <td>
                          <Pill value={binding.status} />
                        </td>
                        <td>{rule ? (rule.enabled === false ? '已暂停' : '已配置') : '待配置'}</td>
                        <td>{rule ? safeText(rule.priority) : '—'}</td>
                        <td>{rule ? safeText(rule.weight) : '—'}</td>
                        <td>
                          <button
                            disabled={unavailable || !qualified}
                            onClick={() => selectBinding(id)}
                          >
                            {bindingId === id ? '正在编辑' : '设置策略'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </State>
          <section className="r4-inline-editor" aria-label="策略参数">
            <h3>策略参数</h3>
            <label>
              选择合法绑定
              <select
                aria-label="选择合法绑定"
                value={bindingId}
                disabled={unavailable}
                onChange={(event) => selectBinding(event.target.value)}
              >
                <option value="">请选择收款商户</option>
                {bindings.map((binding) => (
                  <option
                    key={textOf(binding, 'id')}
                    value={textOf(binding, 'id')}
                    disabled={!['ENABLED', 'DEGRADED'].includes(textOf(binding, 'status'))}
                  >
                    {merchantName(binding)} · {mchid(binding)}
                  </option>
                ))}
              </select>
            </label>
            {bindingId ? (
              <>
                <div className="r4-two">
                  <label>
                    优先级
                    <input
                      aria-label="优先级"
                      type="number"
                      step="1"
                      disabled={unavailable}
                      value={priority}
                      onChange={(event) => setPriority(event.target.value)}
                    />
                  </label>
                  <label>
                    权重
                    <input
                      aria-label="权重"
                      type="number"
                      min="1"
                      step="1"
                      disabled={unavailable}
                      value={weight}
                      onChange={(event) => setWeight(event.target.value)}
                    />
                  </label>
                </div>
                <label>
                  参与新收款
                  <select
                    aria-label="参与新收款"
                    value={ruleEnabled ? 'enabled' : 'disabled'}
                    disabled={unavailable}
                    onChange={(event) => setRuleEnabled(event.target.value === 'enabled')}
                  >
                    <option value="enabled">启用</option>
                    <option value="disabled">暂停</option>
                  </select>
                </label>
                <p className="r4-help">数字越大优先级越高；同级商户按权重及健康情况分配。</p>
                {!validNumbers && (
                  <p className="r4-help error" role="alert">
                    优先级需为整数，权重需为大于 0 的整数。
                  </p>
                )}
                <button
                  className="primary"
                  disabled={unavailable || !eligible || !validNumbers}
                  onClick={() => void save()}
                >
                  {busy ? '保存中…' : '保存策略'}
                </button>
              </>
            ) : (
              <p className="r4-help">在左侧选择已核验的商户，编辑后保存。</p>
            )}
            {Boolean(rulesState.error) && (
              <div role="alert" className="r4-help error">
                策略读取失败。<button onClick={rulesState.reload}>重新加载</button>
              </div>
            )}
            {result && (
              <p className="r4-help" role="status">
                {result}
              </p>
            )}
          </section>
        </div>
      </Panel>
      <p className="r4-footnote">
        未核验、停用或冷却中的商户不参与新收款；已创建支付及退款始终使用原商户。
      </p>
    </div>
  );
}

function healthEventPath(event: JsonRecord, appId: string) {
  const origin = String(event.originAppId ?? event.appId ?? appId);
  return (
    pathFor('ADM-G06', { id: textOf(event, 'id') }) +
    (origin && origin !== AUTHORIZED_SCOPE ? '?appId=' + encodeURIComponent(origin) : '')
  );
}
function Health({
  appId,
  snapshotsState,
  eventsState,
}: {
  appId: string;
  snapshotsState: LoadState;
  eventsState: LoadState;
}) {
  const snapshots = rows(snapshotsState.data);
  const events = rows(eventsState.data);
  const scores = snapshots.map((item) => Number(item.healthScore ?? 0));
  const linkedEvents = events.filter((event) =>
    snapshots.some(
      (snapshot) =>
        (snapshot.bindingId && snapshot.bindingId === event.bindingId) ||
        (snapshot.merchantId && snapshot.merchantId === event.merchantId),
    ),
  );
  return (
    <div className="r4-page r4-workspace">
      <Header title="商户健康监控" subtitle="支付中心　/　商户健康监控" />
      <Metrics
        items={[
          {
            label: '正常',
            value: snapshots.filter((item) => Number(item.healthScore ?? 0) >= 85).length,
          },
          {
            label: '观察',
            value: snapshots.filter(
              (item) => Number(item.healthScore ?? 0) < 85 && Number(item.healthScore ?? 0) >= 70,
            ).length,
          },
          {
            label: '降级 / 停用',
            value: snapshots.filter((item) => Number(item.healthScore ?? 0) < 70).length,
          },
          {
            label: '平均健康分',
            value: scores.length
              ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length)
              : '—',
          },
          { label: '可追溯事件', value: events.length },
        ]}
      />
      <PaymentTabs pageId="ADM-G05" />
      <div className="r4-health-layout">
        <Panel title="商户健康概览">
          <State state={snapshotsState} count={snapshots.length} noun="健康快照">
            <ProgressList
              items={snapshots.map((item) => ({
                label: `${mchid(item)} · ${merchantName(item)}`,
                value: Number(item.healthScore ?? 0),
                note: `${safeText(item.healthScore, '—')} 分`,
                ...(Number(item.healthScore ?? 0) < 70 ? { color: '#ef4444' } : {}),
              }))}
            />
          </State>
        </Panel>
        <Panel title="当前告警" hint={`${linkedEvents.length} 个可追溯事件`}>
          <State state={eventsState} count={linkedEvents.length} noun="健康事件">
            <div className="table-wrap r4-table">
              <table>
                <thead>
                  <tr>
                    <th>发生时间</th>
                    <th>健康信号</th>
                    <th>计入健康</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedEvents.map((event) => (
                    <tr key={textOf(event, 'id')}>
                      <td>{dateTime(event.occurredAt)}</td>
                      <td>{safeText(event.signalCode, '健康信号')}</td>
                      <td>
                        <Pill
                          value={event.affectsHealth === true ? 'FAILED' : 'ENABLED'}
                          text={event.affectsHealth === true ? '是' : '否'}
                        />
                      </td>
                      <td>
                        <NavLink href={healthEventPath(event, appId)}>查看并处置</NavLink>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </State>
        </Panel>
      </div>
      <p className="r4-footnote">
        授权、证书与系统错误参与健康评估；用户取消、余额不足及未完成支付不降低商户健康分。
      </p>
    </div>
  );
}

function HealthEvent({
  api,
  appId,
  event,
  binding,
  eventsState,
  reload,
}: {
  api: AdminApiClient;
  appId: string;
  event: JsonRecord;
  binding: JsonRecord;
  eventsState: LoadState;
  reload: () => void;
}) {
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  async function changeStatus(status: 'ENABLED' | 'DISABLED') {
    if (!safeText(binding.id, '') || !reason.trim() || !confirmed || busy) return;
    setBusy(true);
    setMessage('');
    try {
      await api.send(
        `/api/admin/m3/apps/${encodeURIComponent(appId)}/payment-bindings/${encodeURIComponent(safeText(binding.id))}/status`,
        'PUT',
        {
          status,
          reasonCode: reason.trim(),
          expectedVersion: Number(binding.version),
        },
      );
      setMessage(
        status === 'ENABLED'
          ? '绑定已恢复，健康基线已由服务端重置。'
          : '绑定已停用，不再参与新支付路由。',
      );
      setConfirmed(false);
      reload();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '处置失败，请刷新后重试。');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="r4-page r4-workspace">
      <Header title="商户健康事件详情" subtitle="支付中心　/　商户健康事件详情" />
      <Notice>自动降级、停用必须完整记录原因、时间窗、受影响交易与操作者，并进入统一审计。</Notice>
      <div className="r4-side-layout wide-main">
        <div>
          <Panel
            title={shortRef(event.id, '健康事件')}
            hint={event.affectsHealth === true ? '已计入健康' : '未计入健康'}
          >
            <State state={eventsState} count={Object.keys(event).length} noun="健康事件">
              <div className="r4-event-hero">
                <b>!</b>
                <div>
                  <h2>{safeText(event.signalCode, '商户健康异常')}</h2>
                  <p>
                    <Pill
                      value={safeText(event.severity, 'OBSERVE')}
                      text={safeText(event.severity, '待分级')}
                    />
                  </p>
                </div>
              </div>
              <dl className="r4-kv two">
                <div>
                  <dt>发生时间</dt>
                  <dd>{dateTime(event.occurredAt)}</dd>
                </div>
                <div>
                  <dt>健康分类</dt>
                  <dd>{event.affectsHealth === true ? '计入商户健康' : '不计入商户健康'}</dd>
                </div>
                <div>
                  <dt>信号类别</dt>
                  <dd>{safeText(event.signalClass)}</dd>
                </div>
                <div>
                  <dt>追踪关联</dt>
                  <dd>{event.traceId ? '已记录，可审计追踪' : '暂未记录'}</dd>
                </div>
                <div>
                  <dt>影响绑定</dt>
                  <dd>{event.bindingId ? '已关联具体绑定' : '未关联绑定'}</dd>
                </div>
                <div>
                  <dt>影响商户</dt>
                  <dd>{event.merchantId ? '已关联具体商户' : '未关联商户'}</dd>
                </div>
              </dl>
            </State>
          </Panel>
          <Panel title="错误样本" hint="敏感响应已脱敏">
            <div className="r4-code-safe">
              系统仅保存安全摘要和追踪关联；页面不直接输出原始凭据、签名材料或完整请求载荷。
            </div>
          </Panel>
        </div>
        <aside>
          <Panel title="自动保护决策">
            <div className="r4-timeline compact">
              <article>
                <i />
                <div>
                  <time>{dateTime(event.occurredAt)}</time>
                  <strong>记录健康信号</strong>
                  <span>PostgreSQL 权威事实</span>
                </div>
              </article>
              <article>
                <i />
                <div>
                  <strong>评估保护状态</strong>
                  <span>
                    {event.affectsHealth === true ? '参与自动保护计算' : '不改变商户健康'}
                  </span>
                </div>
              </article>
            </div>
          </Panel>
          <Panel title="人工处置">
            <label>
              处置原因
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="填写探测证据、恢复依据或停用原因"
              />
            </label>
            <label className="r4-check">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
              />
              我已核对商户状态和关联交易，并确认执行高风险变更
            </label>
            <button
              className="primary"
              disabled={
                busy ||
                !reason.trim() ||
                !confirmed ||
                !safeText(binding.id, '') ||
                binding.status === 'ENABLED'
              }
              onClick={() => void changeStatus('ENABLED')}
            >
              确认恢复
            </button>
            <button
              className="danger"
              disabled={
                busy ||
                !reason.trim() ||
                !confirmed ||
                !safeText(binding.id, '') ||
                binding.status === 'DISABLED'
              }
              onClick={() => void changeStatus('DISABLED')}
            >
              停用绑定
            </button>
            <small className="r4-help">
              新绑定的 AppID×MchID 验证仍在“支付绑定”页面执行；此处只处置已验证绑定。
            </small>
            {message && <small className="r4-help">{message}</small>}
          </Panel>
        </aside>
      </div>
    </div>
  );
}

function Transactions({ state }: { state: LoadState }) {
  const items = rows(state.data);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const visible = items.filter(
    (item) =>
      `${textOf(item, 'id')} ${textOf(item, 'outTradeNo')} ${textOf(item, 'wechatTransactionId')}`
        .toLowerCase()
        .includes(search.trim().toLowerCase()) &&
      (statusFilter === 'ALL' ||
        (statusFilter === 'SUCCESS'
          ? ['SUCCEEDED', 'SUCCESS'].includes(textOf(item, 'status'))
          : textOf(item, 'status') === statusFilter)),
  );
  const success = items.filter((item) => ['SUCCEEDED', 'SUCCESS'].includes(textOf(item, 'status')));
  return (
    <div className="r4-page r4-workspace">
      <Header title="支付交易流水" subtitle="支付中心　/　支付交易流水" />
      <Metrics
        items={[
          { label: '当前支付流水', value: items.length },
          { label: '服务端确认成功', value: success.length },
          {
            label: '确认金额',
            value: money(success.reduce((sum, item) => sum + Number(item.amountCents ?? 0), 0)),
          },
          {
            label: '待确认',
            value: items.filter((item) => textOf(item, 'status') === 'PENDING').length,
          },
          {
            label: '系统失败',
            value: items.filter((item) => textOf(item, 'status') === 'FAILED').length,
          },
        ]}
      />
      <PaymentTabs pageId="ADM-G07" />
      <Panel
        title="支付交易流水"
        hint={`显示 ${visible.length} / ${items.length} 条；以支付查单与回调为准`}
        toolbar={
          <Filters search={search} setSearch={setSearch} placeholder="商户单号或微信交易号">
            <select
              aria-label="支付状态"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="ALL">全部状态</option>
              <option value="SUCCESS">支付成功</option>
              <option value="PENDING">待确认</option>
              <option value="FAILED">系统失败</option>
              <option value="CLOSED">未支付</option>
            </select>
          </Filters>
        }
      >
        <State state={state} count={visible.length} noun="支付流水">
          <div className="table-wrap r4-table">
            <table>
              <thead>
                <tr>
                  <th>支付流水 / 商户单号</th>
                  <th>支付商户</th>
                  <th>金额</th>
                  <th>最终状态</th>
                  <th>服务端事实</th>
                  <th>微信交易号</th>
                  <th>创建时间</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((item) => (
                  <tr key={textOf(item, 'id')}>
                    <td>
                      <strong>{shortRef(item.id, '支付')}</strong>
                      <small className="r4-sub">{safeText(item.outTradeNo)}</small>
                    </td>
                    <td>{item.merchantId ? '已固定原支付商户' : '暂未关联'}</td>
                    <td>{money(item.amountCents)}</td>
                    <td>
                      <Pill value={textOf(item, 'status')} />
                    </td>
                    <td>
                      {['SUCCEEDED', 'SUCCESS'].includes(textOf(item, 'status'))
                        ? '服务端已确认'
                        : '等待服务端确认'}
                    </td>
                    <td>
                      {item.wechatTransactionId
                        ? `…${String(item.wechatTransactionId).slice(-8)}`
                        : '—'}
                    </td>
                    <td>{dateTime(item.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
    </div>
  );
}

function Failures({ state }: { state: LoadState }) {
  const items = rows(state.data);
  const [failureType, setFailureType] = useState('ALL');
  const visible = items.filter(
    (item) =>
      failureType === 'ALL' ||
      (failureType === 'SYSTEM' ? item.affectsHealth === true : item.affectsHealth === false),
  );
  const groups = new Map<string, number>();
  items.forEach((item) =>
    groups.set(
      safeText(item.failureCode, '未分类'),
      (groups.get(safeText(item.failureCode, '未分类')) ?? 0) + 1,
    ),
  );
  return (
    <div className="r4-page r4-workspace">
      <Header title="支付失败分析" subtitle="支付中心　/　支付失败分析" />
      <Notice warning>
        必须按“用户行为 / 系统健康”分类；用户取消、余额不足、未完成支付不能作为商户号异常。
      </Notice>
      <Metrics
        items={[
          { label: '失败 / 未支付', value: items.length },
          {
            label: '用户侧结果',
            value: items.filter((item) => item.affectsHealth === false).length,
          },
          {
            label: '系统健康异常',
            value: items.filter((item) => item.affectsHealth === true).length,
          },
          { label: '错误分类', value: groups.size },
          {
            label: '支付挽回候选',
            value: items.filter((item) => item.affectsHealth === false).length,
          },
        ]}
      />
      <PaymentTabs pageId="ADM-G08" />
      <div className="r4-report-layout">
        <Panel title="失败 / 未支付原因分布">
          <ProgressList
            items={Array.from(groups.entries()).map(([key, value]) => ({ label: key, value }))}
          />
        </Panel>
        <Panel
          title="失败样本明细"
          hint={`${visible.length} 条`}
          toolbar={
            <div className="r4-filters">
              <select
                aria-label="失败分类"
                value={failureType}
                onChange={(event) => setFailureType(event.target.value)}
              >
                <option value="ALL">全部分类</option>
                <option value="SYSTEM">系统健康异常</option>
                <option value="USER">用户侧结果</option>
              </select>
            </div>
          }
        >
          <State state={state} count={visible.length} noun="失败样本">
            <div className="table-wrap r4-table">
              <table>
                <thead>
                  <tr>
                    <th>时间</th>
                    <th>支付尝试</th>
                    <th>返回分类</th>
                    <th>错误码</th>
                    <th>是否计入健康</th>
                    <th>处理</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((item) => (
                    <tr key={textOf(item, 'id')}>
                      <td>{dateTime(item.createdAt)}</td>
                      <td>{shortRef(item.id, '支付')}</td>
                      <td>{safeText(item.failureSummary, '未支付')}</td>
                      <td>{safeText(item.failureCode, '未分类')}</td>
                      <td>
                        <Pill
                          value={
                            item.affectsHealth === true
                              ? 'FAILED'
                              : item.affectsHealth === false
                                ? 'ENABLED'
                                : 'PENDING'
                          }
                          text={
                            item.affectsHealth === true
                              ? '是'
                              : item.affectsHealth === false
                                ? '否'
                                : '待分类'
                          }
                        />
                      </td>
                      <td>
                        {item.affectsHealth === true
                          ? '进入健康评估'
                          : item.affectsHealth === false
                            ? '仅运营分析'
                            : '等待分类'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </State>
        </Panel>
      </div>
    </div>
  );
}

function Protection({ state, appId }: { state: LoadState; appId: string }) {
  const items = rows(state.data);
  const [search, setSearch] = useState('');
  const visible = items.filter((item) =>
    `${textOf(item, 'signalCode')} ${shortRef(item.id, '保护')}`
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );
  return (
    <div className="r4-page r4-workspace">
      <Header title="支付自动保护记录" subtitle="支付中心　/　支付自动保护记录" />
      <Metrics
        items={[
          { label: '保护事件', value: items.length },
          { label: '计入健康', value: items.filter((item) => item.affectsHealth === true).length },
          {
            label: '高风险信号',
            value: items.filter((item) => String(item.severity).toUpperCase() === 'HIGH').length,
          },
          {
            label: '当前冷却中',
            value: items.filter(
              (item) => new Date(String(item.cooldownUntil ?? '')).getTime() > Date.now(),
            ).length,
          },
        ]}
      />
      <PaymentTabs pageId="ADM-G10" />
      <Panel
        title="保护记录"
        hint={`显示 ${visible.length} / ${items.length} 条`}
        toolbar={<Filters search={search} setSearch={setSearch} placeholder="保护编号或触发原因" />}
      >
        <State state={state} count={visible.length} noun="保护记录">
          <div className="table-wrap r4-table">
            <table>
              <thead>
                <tr>
                  <th>保护记录</th>
                  <th>时间</th>
                  <th>状态变化</th>
                  <th>触发原因</th>
                  <th>证据分类</th>
                  <th>系统动作</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((item) => (
                  <tr key={textOf(item, 'id')}>
                    <td>{shortRef(item.id, '保护')}</td>
                    <td>{dateTime(item.occurredAt)}</td>
                    <td>
                      <Pill
                        value={safeText(item.severity, 'OBSERVE')}
                        text={safeText(item.severity, '待分级')}
                      />
                    </td>
                    <td>{safeText(item.signalCode)}</td>
                    <td>{item.affectsHealth === true ? '系统健康证据' : '不计入健康'}</td>
                    <td>
                      {item.cooldownUntil
                        ? `冷却至 ${dateTime(item.cooldownUntil)}`
                        : '记录并持续探测'}
                    </td>
                    <td>
                      <a href={healthEventPath(item, appId)}>事件详情</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </State>
      </Panel>
      <p className="r4-footnote">
        用户取消、余额不足和未完成支付不触发商户降级；恢复及停用在事件详情中核对证据后执行。
      </p>
    </div>
  );
}

export function CanonicalPaymentPage(props: PageProps) {
  const access = useApi(props.api, '/api/admin/m8/security/me');
  const menuIds = unwrapRecord(access.data).visibleMenuIds;
  const allowedMenus = Array.isArray(menuIds)
    ? menuIds.filter((id): id is string => typeof id === 'string')
    : [];
  return (
    <PaymentMenuContext.Provider value={allowedMenus}>
      <PaymentPageContent {...props} />
    </PaymentMenuContext.Provider>
  );
}
function PaymentPageContent({ api, targetAppId, pageId, params }: PageProps) {
  const merchantsState = useApi(
    api,
    targetAppId && ['ADM-G01', 'ADM-G02', 'ADM-G03'].includes(pageId)
      ? `/api/admin/m3/payment-merchants?appId=${encodeURIComponent(targetAppId)}`
      : undefined,
  );
  const merchantState = useApi(
    api,
    targetAppId && pageId === 'ADM-G02' && params.id
      ? `/api/admin/m3/payment-merchants/${encodeURIComponent(params.id)}?appId=${encodeURIComponent(targetAppId)}`
      : undefined,
  );
  const merchantBindingsState = useApi(
    api,
    targetAppId && pageId === 'ADM-G02' && params.id
      ? `/api/admin/m3/payment-merchants/${encodeURIComponent(params.id)}/app-bindings?appId=${encodeURIComponent(targetAppId)}`
      : undefined,
  );
  const appsState = useApi(api, pageId === 'ADM-G02' ? '/api/admin/m2/apps' : undefined);
  const bindingsState = useApi(
    api,
    targetAppId && ['ADM-G01', 'ADM-G03', 'ADM-G04', 'ADM-G06'].includes(pageId)
      ? `/api/admin/m3/apps/${encodeURIComponent(targetAppId)}/payment-bindings`
      : undefined,
  );
  const rulesState = useApi(
    api,
    targetAppId && pageId === 'ADM-G04'
      ? `/api/admin/m3/apps/${encodeURIComponent(targetAppId)}/payment-route-rules`
      : undefined,
  );
  const healthState = useApi(
    api,
    targetAppId && ['ADM-G01', 'ADM-G02', 'ADM-G05'].includes(pageId)
      ? `/api/admin/m3/apps/${encodeURIComponent(targetAppId)}/merchant-health`
      : undefined,
  );
  const eventsState = useApi(
    api,
    targetAppId && ['ADM-G05', 'ADM-G06'].includes(pageId)
      ? `/api/admin/m3/apps/${encodeURIComponent(targetAppId)}/merchant-health/events`
      : undefined,
  );
  const transactionsState = useApi(
    api,
    targetAppId && pageId === 'ADM-G07'
      ? `/api/admin/m3/apps/${encodeURIComponent(targetAppId)}/payment-transactions`
      : undefined,
  );
  const failuresState = useApi(
    api,
    targetAppId && pageId === 'ADM-G08'
      ? `/api/admin/m3/apps/${encodeURIComponent(targetAppId)}/payment-failures`
      : undefined,
  );
  const protectionState = useApi(
    api,
    targetAppId && pageId === 'ADM-G10'
      ? `/api/admin/m3/apps/${encodeURIComponent(targetAppId)}/payment-protection-events`
      : undefined,
  );
  if (!targetAppId) return <div className="r4-state">请先选择目标小程序。</div>;
  if (pageId === 'ADM-G01')
    return (
      <PaymentMerchantList
        api={api}
        appId={targetAppId}
        merchantsState={merchantsState}
        bindingsState={bindingsState}
        healthState={healthState}
      />
    );
  if (pageId === 'ADM-G02')
    return (
      <MerchantDetail
        api={api}
        appId={targetAppId}
        merchant={unwrapRecord(merchantState.data)}
        onReload={merchantState.reload}
        appsState={appsState}
        merchantBindingsState={merchantBindingsState}
        healthState={healthState}
      />
    );
  if (pageId === 'ADM-G03')
    return (
      <BindingManager
        api={api}
        appId={targetAppId}
        bindingsState={bindingsState}
        merchantsState={merchantsState}
      />
    );
  if (pageId === 'ADM-G04')
    return (
      <RouteRules
        api={api}
        appId={targetAppId}
        rulesState={rulesState}
        bindingsState={bindingsState}
      />
    );
  if (pageId === 'ADM-G05')
    return <Health appId={targetAppId} snapshotsState={healthState} eventsState={eventsState} />;
  if (pageId === 'ADM-G06')
    return (
      <HealthEvent
        api={api}
        appId={targetAppId}
        event={rows(eventsState.data).find((item) => textOf(item, 'id') === params.id) ?? {}}
        binding={
          rows(bindingsState.data).find(
            (item) =>
              textOf(item, 'id') ===
              textOf(
                rows(eventsState.data).find((candidate) => textOf(candidate, 'id') === params.id) ??
                  {},
                'bindingId',
              ),
          ) ?? {}
        }
        eventsState={eventsState}
        reload={() => {
          bindingsState.reload();
          eventsState.reload();
        }}
      />
    );
  if (pageId === 'ADM-G07') return <Transactions state={transactionsState} />;
  if (pageId === 'ADM-G08') return <Failures state={failuresState} />;
  return <Protection appId={targetAppId} state={protectionState} />;
}
